import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Check, X, ArrowLeft } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const Circle = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();

  const handleJoin = () => {
    if (!isAuthenticated) {
      // Store intent and redirect to sign-in
      localStorage.setItem('ohmnai_redirect_intent', '/checkout');
      navigate('/auth/email');
    } else {
      // User is authenticated, go to checkout
      navigate('/checkout');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-mist via-mist-light to-fern">
      {/* Header */}
      <header className="border-b border-calm-border bg-fern-card/80 backdrop-blur-sm">
        <div className="max-w-5xl mx-auto px-6 py-5 flex justify-between items-center">
          <div 
            className="text-2xl font-bold text-calm-text cursor-pointer"
            style={{fontFamily: 'DM Serif Display, serif'}}
            onClick={() => navigate('/')}
          >
            OHMNAI
          </div>
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="border-calm-border text-forest hover:bg-fern"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Dashboard
          </Button>
        </div>
      </header>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-16">
        <div className="text-center mb-16">
          <h1 className="text-5xl text-calm-text mb-6" style={{fontFamily: 'DM Serif Display, serif'}}>
            OHMNAI Circle
          </h1>
          <p className="text-xl text-calm-text-secondary max-w-2xl mx-auto">
            A community of quiet stewards supporting long-term alignment work.
          </p>
        </div>

        {/* Comparison */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          {/* Free Plan */}
          <div className="bg-fern-card rounded-2xl p-8 border border-calm-border shadow-sm">
            <h2 className="text-2xl text-calm-text mb-2" style={{fontFamily: 'DM Serif Display, serif'}}>Free</h2>
            <p className="text-calm-text-secondary mb-8">Always available, no commitment</p>
            
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-herbal mt-0.5 flex-shrink-0" />
                <span className="text-calm-text">Full values profile</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-herbal mt-0.5 flex-shrink-0" />
                <span className="text-calm-text">Occasional action suggestions</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-herbal mt-0.5 flex-shrink-0" />
                <span className="text-calm-text">Transparency on reasoning</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-herbal mt-0.5 flex-shrink-0" />
                <span className="text-calm-text">Export and delete anytime</span>
              </li>
              <li className="flex items-start gap-3">
                <X className="h-5 w-5 text-calm-border mt-0.5 flex-shrink-0" />
                <span className="text-calm-text-secondary/60">Reduced frequency</span>
              </li>
              <li className="flex items-start gap-3">
                <X className="h-5 w-5 text-calm-border mt-0.5 flex-shrink-0" />
                <span className="text-calm-text-secondary/60">No pooled actions</span>
              </li>
              <li className="flex items-start gap-3">
                <X className="h-5 w-5 text-calm-border mt-0.5 flex-shrink-0" />
                <span className="text-calm-text-secondary/60">No influence layer</span>
              </li>
            </ul>
          </div>

          {/* Circle Plan */}
          <div className="bg-gradient-to-br from-forest to-forest-dark rounded-2xl p-8 text-white shadow-xl border-2 border-forest-light">
            <h2 className="text-2xl mb-2" style={{fontFamily: 'DM Serif Display, serif'}}>Circle</h2>
            <p className="text-3xl font-light mb-2">$9<span className="text-lg text-herbal-light">/month</span></p>
            <p className="text-herbal-light mb-8">Stewardship, not premium</p>
            
            <ul className="space-y-4 mb-8">
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-herbal mt-0.5 flex-shrink-0" />
                <span className="text-white">Everything in Free</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-herbal mt-0.5 flex-shrink-0" />
                <span className="text-white">Long-term alignment memory</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-herbal mt-0.5 flex-shrink-0" />
                <span className="text-white">Pooled action suggestions</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-herbal mt-0.5 flex-shrink-0" />
                <span className="text-white">Quiet sponsorship opportunities</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-herbal mt-0.5 flex-shrink-0" />
                <span className="text-white">Platform influence and input</span>
              </li>
              <li className="flex items-start gap-3">
                <Check className="h-5 w-5 text-herbal mt-0.5 flex-shrink-0" />
                <span className="text-white">Member-only experiments</span>
              </li>
            </ul>

            <Button
              onClick={handleJoin}
              className="w-full bg-white text-forest hover:bg-fern-card py-6 text-base rounded-xl transition-colors shadow-lg"
            >
              Join Circle
            </Button>

            <p className="text-sm text-herbal-light/80 text-center mt-4">
              Cancel anytime, no questions asked
            </p>
          </div>
        </div>

        {/* Philosophy */}
        <div className="bg-fern-card rounded-2xl p-8 border border-calm-border shadow-sm">
          <h3 className="text-xl font-medium text-calm-text mb-4" style={{fontFamily: 'DM Serif Display, serif'}}>Why $9/month?</h3>
          <div className="space-y-4 text-forest leading-relaxed">
            <p>
              Circle membership isn't a premium tier. It's stewardship.
            </p>
            <p>
              The free version will always remain meaningful. Circle members help sustain the platform and gain access to features that require deeper computational resources—long-term memory, pooled actions, and ongoing experiments.
            </p>
            <p>
              No trials. No upsells. No hidden fees. Just transparent support for alignment work that doesn't extract or manipulate.
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="border-t border-calm-border bg-fern-card/50 mt-20">
        <div className="max-w-6xl mx-auto px-6 py-12">
          <div className="flex justify-between items-center">
            <div className="text-calm-text-secondary text-sm">
              © 2025 OHMNAI. Who you are is what you choose to enable.
            </div>
            <div className="flex gap-8 text-sm text-calm-text-secondary">
              <a href="/why" className="hover:text-forest transition-colors">About</a>
              <a href="#" className="hover:text-forest transition-colors">Privacy</a>
              <a href="/vision" className="hover:text-forest transition-colors">Vision</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Circle;
